import React from 'react'
import { useMessaging, formatTimeDiff } from './MessagingProvider'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '~/util/api'
import { cn } from '~/lib/utils'
import parse from 'html-react-parser'
import useTranslation from '../shared/TranslationProvider'

const supportMessagesPath = '/support_messages'

const SentList = () => {
  const { t } = useTranslation('CoachMessageThread')
  const { error, data: sentMessages } = useQuery({
    queryKey: ['sentMessages'],
    queryFn: () =>
      apiGet(`${supportMessagesPath}.json`).then((res) => res.data),
    refetchInterval: 10000,
  })

  if (error) return t('an_error_occurred') + error.message

  return (
    <div className="flex flex-col gap-2 p-4 pt-0">
      {sentMessages?.length === 0 && (
        <div className="flex h-full text-center text-sm">
          {t('sent_messages_appear_here')}
        </div>
      )}
      {sentMessages?.map((message) => (
        <SentMessageButton key={message.id} message={message} />
      ))}
    </div>
  )
}
export default SentList

const SentMessageButton = ({ message }) => {
  const { currentSentMessage, setCurrentSentMessage, clearNewMessageMode } =
    useMessaging()
  const {
    created_at: sentAt,
    id: messageId,
    subject,
    recipient_full_name: otherUserName,
  } = message
  const isSelected = messageId === currentSentMessage.id
  const changeCurrentMessage = () => {
    setCurrentSentMessage(message)
    clearNewMessageMode()
  }

  return (
    <button
      className={cn(
        'flex flex-col items-start gap-2 rounded-3xl border border-silver px-6 py-4 text-left text-sm text-intoo-text transition-all hover:bg-willow-brook hover:text-intoo-text focus:text-intoo-text',
        isSelected &&
          'bg-intoo-green-coachcta text-white hover:bg-intoo-green-coachcta-hover hover:text-white focus:text-white',
      )}
      onClick={changeCurrentMessage}
    >
      <div className="flex w-full flex-col gap-1">
        <div className="flex items-center">
          <div className="flex items-center gap-2">
            <div className="text-lg font-semibold">{otherUserName}</div>
          </div>
          <div className="ml-auto text-xs">{formatTimeDiff(sentAt)}</div>
        </div>
        <div className="text-xs font-medium">{subject}</div>
      </div>
    </button>
  )
}

export const SentMessage = () => {
  const { t } = useTranslation('CoachMessageThread')
  const {
    currentSentMessage: {
      recipient_full_name: userFullName,
      subject,
      body,
      attachment = {},
      attachment_filename: attachmentFilename,
    },
  } = useMessaging()

  const { url: attachmentUrl } = attachment

  if (!(subject || body)) return null

  return (
    <div className="flex h-full flex-col overflow-y-hidden rounded-3xl border border-silver bg-white p-4">
      <div className="relative -mx-4 -mt-4 flex items-center justify-between border-b bg-intoo-green-coachcta px-6 py-4">
        <div className="font-roboto-slab text-white md:text-2xl">
          {t('subject_line')} {subject}
        </div>
      </div>
      <div className="flex flex-col py-2">
        <div className="py-">
          {t('to_line')} {userFullName}
        </div>
        <div>
          {parse(body.replace(/&nbsp;/g, ' '))}
          {attachmentUrl && (
            <a
              rel="noreferrer"
              href={attachmentUrl}
              className="my-2 inline-block"
              download
            >
              {attachmentFilename}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
